import { useState } from "react";
import T from "../../theme";
import { PRIORITY_COLORS } from "../../constants";
import { ContactModal } from "./ContactModal";

/**
 * DemoOverlay — shown over blocked features for demo/invited users.
 */
export function DemoOverlay({ message, compact = false }) {
  if (compact) {
    return (
      <div style={{
        position: "absolute", inset: 0, zIndex: 10,
        background: T.card, opacity: 0.75,
        backdropFilter: "blur(2px)", borderRadius: "inherit",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "not-allowed",
      }}>
        <span style={{ fontSize: 18 }}>🔒</span>
      </div>
    );
  }

  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 20,
      background: T.bg, opacity: 0.85,
      backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", gap: 12, borderRadius: 12,
    }}>
      <div style={{
        background: T.card, border: `1px solid ${T.border}`,
        borderRadius: 16, padding: "32px 40px",
        textAlign: "center", maxWidth: 380, boxShadow: T.shadowLg,
      }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
        <p style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 8px" }}>
          Funcionalidade Premium
        </p>
        <p style={{ fontSize: 13, color: T.textMuted, margin: 0, lineHeight: 1.6 }}>
          {message || "Esta funcionalidade não está disponível na versão de demonstração. Contacte-nos para saber mais."}
        </p>
      </div>
    </div>
  );
}

/* ─── Demo ticket preview row ───────────────────────────────────────────── */
const TicketPreview = ({ t }) => {
  const prioColor = PRIORITY_COLORS[t.priority] || "#6b7280";
  const statusColors = { "Aberto": "#2563eb", "Em tratamento": "#7c3aed", "Resolvido": "#059669", "Concluído": "#6b7280" };
  const staColor = statusColors[t.status] || "#6b7280";

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 14px", borderRadius: 8,
      background: T.card, border: `1px solid ${T.border}`,
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: "50%",
        background: prioColor, flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {t.title}
        </p>
        <p style={{ fontSize: 11, color: T.textMuted, margin: "2px 0 0" }}>
          {t.ownerName || "Utilizador demo"}
        </p>
      </div>
      <span style={{
        fontSize: 11, fontWeight: 600, color: staColor,
        background: `${staColor}15`, padding: "3px 8px", borderRadius: 6,
        whiteSpace: "nowrap",
      }}>
        {t.status}
      </span>
    </div>
  );
};

/* ─── Demo supplier preview card ────────────────────────────────────────── */
const SupplierPreview = ({ s }) => (
  <div style={{
    padding: "12px 14px", borderRadius: 10,
    background: T.card, border: `1px solid ${T.border}`,
  }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
      <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: 0 }}>{s.name}</p>
      <span style={{
        fontSize: 10, fontWeight: 600, color: T.primary,
        background: T.primaryLight, padding: "2px 7px", borderRadius: 5,
      }}>
        {s.category || s.service}
      </span>
    </div>
    {s.phone && <p style={{ fontSize: 12, color: T.textMuted, margin: "2px 0 0" }}>📞 {s.phone}</p>}
    {s.email && <p style={{ fontSize: 12, color: T.textMuted, margin: "2px 0 0" }}>✉️ {s.email}</p>}
  </div>
);

/**
 * DemoBlockedPage — full-page blocked state with optional data preview.
 * Props:
 *   - title: page title
 *   - icon: emoji/icon
 *   - message: custom message
 *   - demoItems: array of demo data to show as preview
 *   - demoType: "tickets" | "suppliers" — determines preview layout
 */
export function DemoBlockedPage({ title, icon = "🔒", message, demoItems = [], demoType }) {
  const [showContact, setShowContact] = useState(false);
  const hasPreview = demoItems.length > 0 && (demoType === "tickets" || demoType === "suppliers");

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22 }}>{icon}</span>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: T.text, margin: 0 }}>{title}</h2>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 6,
            background: T.primaryLight, color: T.primary, marginLeft: 4,
          }}>
            DEMO
          </span>
        </div>
        <p style={{ color: T.textMuted, fontSize: 13, marginTop: 4, marginLeft: 32 }}>
          Dados de demonstração — apenas para pré-visualização.
        </p>
      </div>

      {/* Preview with overlaid blocked card */}
      {hasPreview ? (
        <div style={{ position: "relative" }}>
          {/* Demo data preview (background) */}
          <div style={{
            display: "grid",
            gridTemplateColumns: demoType === "suppliers" ? "repeat(auto-fill, minmax(260px, 1fr))" : "1fr",
            gap: 8,
            opacity: 0.35,
            pointerEvents: "none",
            userSelect: "none",
          }}>
            {demoType === "tickets" && demoItems.slice(0, 8).map((t, i) => (
              <TicketPreview key={t.id || i} t={t} />
            ))}
            {demoType === "suppliers" && demoItems.slice(0, 6).map((s, i) => (
              <SupplierPreview key={s.id || i} s={s} />
            ))}
          </div>

          {/* Overlaid blocked card */}
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{
              background: T.card, border: `1px solid ${T.border}`,
              borderRadius: 20, padding: "40px 36px",
              textAlign: "center", maxWidth: 420, boxShadow: T.shadowLg,
            }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>🔒</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: "0 0 8px" }}>
                Funcionalidade Premium
              </h3>
              <p style={{ fontSize: 13, color: T.textMuted, margin: "0 0 24px", lineHeight: 1.7 }}>
                {message || "Esta secção não está disponível na versão de demonstração. Subscreva para aceder a todas as funcionalidades."}
              </p>
              <button onClick={() => setShowContact(true)} style={{
                display: "inline-block", background: T.primary, color: "#fff",
                padding: "10px 28px", borderRadius: 8, fontSize: 14, fontWeight: 600,
                border: "none", cursor: "pointer", fontFamily: "inherit",
              }}>
                Contactar VillaGest
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* No preview — just the blocked card centered */
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 32px" }}>
          <div style={{
            background: T.card, border: `1px solid ${T.border}`,
            borderRadius: 20, padding: "40px 36px",
            textAlign: "center", maxWidth: 420, boxShadow: T.shadowLg,
          }}>
            <div style={{ fontSize: 40, marginBottom: 14 }}>🔒</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: "0 0 8px" }}>
              Funcionalidade Premium
            </h3>
            <p style={{ fontSize: 13, color: T.textMuted, margin: "0 0 24px", lineHeight: 1.7 }}>
              {message || "Esta secção não está disponível na versão de demonstração. Subscreva para aceder a todas as funcionalidades."}
            </p>
            <button onClick={() => setShowContact(true)} style={{
              display: "inline-block", background: T.primary, color: "#fff",
              padding: "10px 28px", borderRadius: 8, fontSize: 14, fontWeight: 600,
              border: "none", cursor: "pointer", fontFamily: "inherit",
            }}>
              Contactar VillaGest
            </button>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
    </div>
  );
}
